import React, { useRef, useState } from 'react';

import {
  Accordion,
  accordionClasses,
  AccordionDetails,
  AccordionSummary,
  accordionSummaryClasses,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Modal,
  paperClasses,
  styled,
  useMediaQuery,
} from '@mui/material';

import { FilterIcon } from '../../../assets/icons';
import { useClickOutside } from '../../hooks/click-outside';
import { CloseButton } from '../buttons/close-button';
import { Tag } from '../tags/tags';

type FilterOption = { name: string; selected: boolean };
type FilterCategory = {
  name: string;
  options: FilterOption[];
};
export type FilterData<T extends string> = Record<T, FilterCategory>;

export type FilterTranslations = {
  filter_flyout_reset_all: string;
  filter_flyout_headline_selected: string;
  general_filter_results: string;
};

export const Filter = <T extends string>({
  className = '',
  filterOptions,
  onChange,
  filteredItemsCountTranslation,
  translations,
}: {
  className?: string;
  filterOptions: FilterData<T>;
  onChange: (selectedFilters: FilterData<T>) => void;
  filteredItemsCountTranslation: string;
  translations: FilterTranslations;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  const handleChange = (
    category: keyof FilterData<T>,
    options: FilterOption[],
  ) => {
    onChange({
      ...filterOptions,
      [category]: {
        ...filterOptions[category],
        options,
      },
    });
  };

  const removeCategoryFromFilters = (category: keyof FilterData<T>) => {
    onChange({
      ...filterOptions,
      [category]: {
        ...filterOptions[category],
        options: filterOptions[category].options.map((o) => ({
          ...o,
          selected: false,
        })),
      },
    });
  };

  const handleClear = () => {
    onChange(
      Object.fromEntries(
        Object.entries<FilterCategory>(filterOptions).map(([key, category]) => [
          key as T,
          {
            name: category.name,
            options: category.options.map((o) => ({
              ...o,
              selected: false,
            })),
          },
        ]),
      ) as FilterData<T>,
    );
  };

  return (
    <FilterContainer className={className} ref={filterRef}>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<FilterIcon />}
        onClick={() => setIsOpen(true)}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {translations.general_filter_results}
      </Button>
      {isOpen ? (
        <FlyoutWrapper onClose={() => setIsOpen(false)} filterRef={filterRef}>
          <ContentWrapper>
            <Header>
              <Headline>{translations.general_filter_results}</Headline>
              <CloseButton onClick={() => setIsOpen(false)} />
            </Header>
            {Object.values<FilterCategory>(filterOptions).some((category) =>
              category.options.some((o) => o.selected),
            ) ? (
              <UsedFilterWrapper>
                <UsedFilterHeadline>
                  {translations.filter_flyout_headline_selected}
                </UsedFilterHeadline>
                <UsedFilters>
                  {Object.entries<FilterCategory>(filterOptions)
                    .filter(([, category]) =>
                      category.options.some((o) => o.selected),
                    )
                    .map(([key, filter]) => (
                      <Tag
                        tagTheme="light"
                        key={filter.name}
                        onClick={() => {
                          removeCategoryFromFilters(key as T);
                        }}
                        closeable
                      >
                        {filter.name}
                      </Tag>
                    ))}
                </UsedFilters>
              </UsedFilterWrapper>
            ) : null}
            <FilterCategories>
              {Object.entries<FilterCategory>(filterOptions).map(
                ([key, category]) => (
                  <FilterCategoryItem
                    key={key}
                    title={category.name}
                    options={category.options}
                    onChange={(selected) => handleChange(key as T, selected)}
                  />
                ),
              )}
            </FilterCategories>
            <div
              style={{
                marginTop: 'var(--spacer-l)',
                display: 'flex',
                gap: 'var(--spacer-m)',
                justifyContent: 'stretch',
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                style={{ flexBasis: '100%' }}
                onClick={handleClear}
              >
                {translations.filter_flyout_reset_all}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ flexBasis: '100%' }}
                onClick={() => setIsOpen(false)}
              >
                {filteredItemsCountTranslation}
              </Button>
            </div>
          </ContentWrapper>
        </FlyoutWrapper>
      ) : null}
    </FilterContainer>
  );
};

const FilterCategoryItem = ({
  title,
  options,
  onChange,
}: {
  title: string;
  options: FilterOption[];
  onChange: (selectedOptions: FilterOption[]) => void;
}) => {
  const handleChange = (option: FilterOption, checked: boolean) => {
    const newSelectedOptions = options.map((o) =>
      o.name === option.name ? { ...o, selected: checked } : o,
    );

    if (onChange) {
      onChange(newSelectedOptions);
    }
  };

  return options.length > 0 ? (
    <FilterCategory>
      <FilterCategorySummary expandIcon={<Plus />}>
        <CategoryTitle>{title}</CategoryTitle>
      </FilterCategorySummary>
      <AccordionDetails sx={{ paddingTop: 0 }}>
        {options.map((option, index) => (
          <FormControl
            sx={{
              display: 'grid',
              gridTemplateColumns: 'min-content 1fr',
              alignItems: 'center',
              marginLeft: '-11px',
            }}
            key={index}
          >
            <FormControlLabel
              sx={{
                display: 'contents',
              }}
              label={option.name}
              control={
                <Checkbox
                  checked={options.some(
                    (o) => o.name === option.name && o.selected,
                  )}
                  onChange={(event) =>
                    handleChange(option, event.target.checked)
                  }
                />
              }
            />
          </FormControl>
        ))}
      </AccordionDetails>
    </FilterCategory>
  ) : null;
};

const FlyoutWrapper = ({
  children,
  onClose,
  filterRef,
}: {
  children: React.ReactElement<unknown>;
  onClose: () => void;
  filterRef: React.RefObject<HTMLDivElement>;
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  useClickOutside(filterRef, isMobile ? () => null : onClose);

  return isMobile ? (
    <Modal open>{children}</Modal>
  ) : (
    <FilterFlyout>{children}</FilterFlyout>
  );
};

const FilterCategory = styled(Accordion)(({ theme }) => ({
  [`&.${paperClasses.root}.${accordionClasses.root}:first-of-type`]: {
    borderTop: '1px solid var(--color-dark-coal)',
  },
}));

const FilterCategorySummary = styled(AccordionSummary)(({ theme }) => ({
  [`& .${accordionSummaryClasses.expandIconWrapper} svg`]: {
    width: '36px',
    height: '36px',
  },
}));

const Plus = () => (
  <svg viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      className="mewa-accordion-summary"
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.2504 19.3183V29.0001H18.7504V19.3288L28.4951 19.397L28.5056 17.8971L18.7504 17.8288V8.00006H17.2504V17.8183L7.50562 17.7501L7.49512 19.25L17.2504 19.3183Z"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.2504 19.3183L28.4951 19.397L28.5056 17.8971L18.7504 17.8288V17.8183L7.50562 17.7501L7.49512 19.25L17.2504 19.3183Z"
    />
  </svg>
);

const CategoryTitle = styled('h5')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,
}));

const FilterContainer = styled('div')(({ theme }) => ({
  position: 'relative',
}));

const FilterFlyout = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 'unset',
  top: 0,
  right: 0,
  width: '475px',
  height: 'auto',
  zIndex: 1,

  [theme.breakpoints.up('md')]: {
    width: '575px',
  },

  [theme.breakpoints.up('md')]: {
    width: '625px',
  },
}));

const ContentWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  backgroundColor: 'var(--color-white)',
  padding: 'var(--spacer-l)',
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.17)',
}));

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  borderBottom: '1px solid rgba(211, 211, 211, 1)',
}));

const Headline = styled('h3')(({ theme }) => ({
  ...theme.typography.h5,
  margin: '0 0 var(--spacer-m)',
}));

const UsedFilterWrapper = styled('div')(({ theme }) => ({
  marginTop: 'var(--spacer-m)',
}));

const UsedFilterHeadline = styled('h4')(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightMedium,
  margin: 0,
}));

const UsedFilters = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '10px',
  marginTop: 'var(--spacer-s)',
}));

const FilterCategories = styled('div')(({ theme }) => ({
  marginTop: 'var(--spacer-m)',
}));
